import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatNumber } from "@/lib/format";
import type { ApiCallLog, ApiClient } from "@/pages/admin/apikeys/hooks/useApiKeys";

type WindowDays = 7 | 14 | 30;

const WINDOWS: WindowDays[] = [7, 14, 30];
const RANK_PREVIEW = 6;

const BLOCKED_REASONS = new Set([
  "rate_limited",
  "quota_exceeded",
  "key_revoked",
  "client_suspended",
  "key_expired",
  "invalid_api_key",
  "missing_api_key",
]);

interface ApiUsageInsightsProps {
  logs: ApiCallLog[];
  clients: ApiClient[];
  loading: boolean;
  activeClientId: number | "all";
  onSelectClient: (clientId: number | "all") => void;
}

interface DayBucket {
  key: string;
  ok: number;
  fail: number;
  blocked: number;
  total: number;
}

function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function bucketOf(log: ApiCallLog): "ok" | "fail" | "blocked" {
  if (log.valid === true) return "ok";
  if (log.status_code === 429 || (log.reason && BLOCKED_REASONS.has(log.reason))) return "blocked";
  return "fail";
}

// 调用洞察：按可切换时间窗口（近 7 / 14 / 30 天）展示调用趋势（成功 / 失败 / 被限流堆叠）
// 与对接方调用量排行；点击排行榜某一方即可联动筛选下方调用日志。
export default function ApiUsageInsights({
  logs,
  clients,
  loading,
  activeClientId,
  onSelectClient,
}: ApiUsageInsightsProps) {
  const { t } = useTranslation();
  const [windowDays, setWindowDays] = useState<WindowDays>(14);
  const [rankExpanded, setRankExpanded] = useState(false);

  const days = useMemo<DayBucket[]>(() => {
    const buckets: DayBucket[] = [];
    const index = new Map<string, DayBucket>();
    const now = new Date();
    for (let i = windowDays - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const bucket: DayBucket = { key: localDayKey(d), ok: 0, fail: 0, blocked: 0, total: 0 };
      buckets.push(bucket);
      index.set(bucket.key, bucket);
    }
    logs.forEach((log) => {
      const created = new Date(log.created_at);
      if (Number.isNaN(created.getTime())) return;
      const bucket = index.get(localDayKey(created));
      if (!bucket) return;
      bucket[bucketOf(log)] += 1;
      bucket.total += 1;
    });
    return buckets;
  }, [logs, windowDays]);

  const trendTotal = useMemo(() => days.reduce((sum, day) => sum + day.total, 0), [days]);
  const peak = useMemo(() => days.reduce((max, day) => Math.max(max, day.total), 0), [days]);
  const scaleMax = peak === 0 ? 1 : peak;

  const windowStart = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() - (windowDays - 1)).getTime();
  }, [windowDays]);

  const ranking = useMemo(() => {
    const map = new Map<number, { client: ApiClient; ok: number; fail: number; blocked: number; total: number }>();
    clients.forEach((client) => map.set(client.id, { client, ok: 0, fail: 0, blocked: 0, total: 0 }));
    logs.forEach((log) => {
      if (log.client_id == null) return;
      const created = new Date(log.created_at).getTime();
      if (Number.isNaN(created) || created < windowStart) return;
      const entry = map.get(log.client_id);
      if (!entry) return;
      entry[bucketOf(log)] += 1;
      entry.total += 1;
    });
    return Array.from(map.values())
      .filter((entry) => entry.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [logs, clients, windowStart]);

  const rankTotal = useMemo(() => ranking.reduce((sum, entry) => sum + entry.total, 0), [ranking]);
  const rankMax = ranking.length > 0 ? ranking[0].total : 1;
  const visibleRanking = rankExpanded ? ranking : ranking.slice(0, RANK_PREVIEW);

  const handleRankClick = (clientId: number) => {
    onSelectClient(activeClientId === clientId ? "all" : clientId);
    document.getElementById("api-call-logs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
            <i className="ri-line-chart-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apikeys.usageTitle")}</h2>
            <p className="text-[11px] text-foreground-500">
              {t("apikeys.usageSubWithDays", { days: windowDays })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
            {WINDOWS.map((value) => {
              const active = windowDays === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setWindowDays(value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    active ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
                  }`}
                >
                  {t("apikeys.usageWindowShort", { days: value })}
                </button>
              );
            })}
          </div>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-pulse-line" />
            </span>
            {t("apikeys.usageWindowTotal", { days: windowDays, count: formatNumber(trendTotal) })}
          </span>
        </div>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-5">
        {/* 趋势图 */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground-800">
              {t("apikeys.trendTitle", { days: windowDays })}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-primary-500" />
                {t("apikeys.trendLegendOk")}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-secondary-400" />
                {t("apikeys.trendLegendFail")}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-accent-400" />
                {t("apikeys.trendLegendBlocked")}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="mt-3 h-28 rounded-md bg-background-100" />
          ) : trendTotal === 0 ? (
            <div className="mt-3 flex h-28 items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100 text-xs text-foreground-500">
              {t("apikeys.trendEmpty")}
            </div>
          ) : (
            <>
              <div className="mt-3 flex h-28 items-stretch gap-1">
                {days.map((day) => (
                  <div
                    key={day.key}
                    className="group flex flex-1 flex-col justify-end gap-px"
                    title={`${formatDate(day.key)} · ${t("apikeys.trendDayTotal", { count: day.total })}`}
                  >
                    {day.blocked > 0 && (
                      <div
                        className="w-full rounded-sm bg-accent-400"
                        style={{ height: `${(day.blocked / scaleMax) * 100}%` }}
                      />
                    )}
                    {day.fail > 0 && (
                      <div
                        className="w-full rounded-sm bg-secondary-400"
                        style={{ height: `${(day.fail / scaleMax) * 100}%` }}
                      />
                    )}
                    {day.ok > 0 && (
                      <div
                        className="w-full rounded-sm bg-primary-500"
                        style={{ height: `${(day.ok / scaleMax) * 100}%` }}
                      />
                    )}
                    {day.total === 0 && <div className="h-0.5 w-full rounded-sm bg-background-200" />}
                  </div>
                ))}
              </div>
              <div className="mt-1.5 flex items-stretch gap-1">
                {days.map((day, index) => (
                  <span key={day.key} className="flex-1 text-center text-[9px] text-foreground-400">
                    {index % 2 === 0 || index === days.length - 1 ? formatDate(day.key) : ""}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-foreground-500">
                {t("apikeys.trendPeak", { count: formatNumber(peak) })}
              </p>
            </>
          )}
        </div>

        {/* 排行 */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground-800">{t("apikeys.rankTitle")}</p>
            <span className="text-[10px] text-foreground-500">
              {t("apikeys.rankTotal", { count: formatNumber(rankTotal) })}
            </span>
          </div>
          <p className="mt-1 text-[10px] text-foreground-400">{t("apikeys.rankClickHint")}</p>

          {loading ? (
            <div className="mt-3 flex flex-col gap-2">
              {[0, 1, 2].map((item) => (
                <div key={item} className="h-10 rounded-md bg-background-100" />
              ))}
            </div>
          ) : ranking.length === 0 ? (
            <div className="mt-3 flex h-28 items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100 text-xs text-foreground-500">
              {t("apikeys.rankEmpty")}
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2.5">
              {visibleRanking.map((entry, index) => {
                const share = rankTotal === 0 ? 0 : (entry.total / rankTotal) * 100;
                const selected = activeClientId === entry.client.id;
                return (
                  <button
                    key={entry.client.id}
                    type="button"
                    onClick={() => handleRankClick(entry.client.id)}
                    className={`w-full cursor-pointer rounded-md border px-2.5 py-2 text-left transition-colors ${
                      selected
                        ? "border-primary-300 bg-primary-50"
                        : "border-transparent hover:border-background-300 hover:bg-background-100"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold ${
                            index === 0
                              ? "bg-accent-100 text-accent-800"
                              : selected
                                ? "bg-primary-100 text-primary-700"
                                : "bg-background-100 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="truncate text-xs font-medium text-foreground-900">{entry.client.name}</span>
                        {selected && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700">
                            <i className="ri-filter-3-line" />
                            {t("apikeys.rankActive")}
                          </span>
                        )}
                      </div>
                      <span className="shrink-0 text-[11px] text-foreground-600">
                        {t("apikeys.rankCalls", { count: formatNumber(entry.total) })}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-100">
                        <div
                          className="h-full rounded-full bg-primary-500"
                          style={{ width: `${Math.max(4, (entry.total / rankMax) * 100)}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-[10px] text-foreground-400">
                        {t("apikeys.rankShare", { pct: share.toFixed(1) })}
                      </span>
                      {entry.blocked > 0 && (
                        <span className="shrink-0 rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800">
                          {t("apikeys.rankBlocked", { count: entry.blocked })}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {ranking.length > RANK_PREVIEW && (
                <button
                  type="button"
                  onClick={() => setRankExpanded((value) => !value)}
                  className="mt-1 inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={rankExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                  </span>
                  {rankExpanded ? t("apikeys.rankCollapse") : t("apikeys.rankShowAll", { count: ranking.length })}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}